import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitApplication, fileToDataUrl } from "@/lib/api";

const JOB_TYPES = [
  "Factory Work",
  "Housekeeping",
  "Industrial Labor",
  "Skilled Operator",
  "Semi-Skilled Worker",
  "Shift-Based Job",
  "Other",
];

const SHIFT_PREFS = [
  "12-hour Shift (Day 8 AM – 8 PM)",
  "12-hour Shift (Night 8 PM – 8 AM)",
  "8-hour Shift (Morning)",
  "8-hour Shift (Afternoon)",
  "8-hour Shift (Night)",
  "Flexible / Any Shift",
];

const EXP_RANGES = [
  "Fresher (0 years)",
  "0–1 year",
  "1–3 years",
  "3–5 years",
  "5+ years",
];

export default function ApplicationForm() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    experience: "",
    job_type: "",
    shift_preference: "",
    message: "",
  });
  const [resume, setResume] = useState(null);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Resume must be under 5MB");
      e.target.value = "";
      return;
    }
    setResume(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.full_name ||
      !form.phone ||
      !form.email ||
      !form.experience ||
      !form.job_type ||
      !form.shift_preference
    ) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (resume) {
        payload.resume_file_name = resume.name;
        payload.resume_file_data = await fileToDataUrl(resume);
      }
      await submitApplication(payload);
      setSuccess(true);
      toast.success("Application received! Our team will reach out shortly.");
      setForm({
        full_name: "",
        phone: "",
        email: "",
        experience: "",
        job_type: "",
        shift_preference: "",
        message: "",
      });
      setResume(null);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Could not submit application. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="application-success"
        className="rounded-sm border border-[#1F6B5C]/30 bg-[#1F6B5C]/5 p-8 text-center"
      >
        <CheckCircle2 className="h-12 w-12 text-[#1F6B5C] mx-auto" />
        <h3 className="font-display text-2xl font-bold text-[#0F172A] mt-4">
          Application submitted
        </h3>
        <p className="text-[#475569] mt-2 max-w-md mx-auto">
          Thank you for applying to RK Enterprises. Our recruitment team will
          contact you within 2–3 working days.
        </p>
        <Button
          onClick={() => setSuccess(false)}
          className="mt-6 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-sm"
          data-testid="application-submit-another"
        >
          Submit another application
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="application-form"
      className="grid grid-cols-1 md:grid-cols-2 gap-5"
    >
      <div className="md:col-span-2">
        <Label htmlFor="full_name">Full Name *</Label>
        <Input
          id="full_name"
          data-testid="application-input-fullname"
          value={form.full_name}
          onChange={update("full_name")}
          placeholder="Enter your full name"
          className="mt-2"
          required
        />
      </div>
      <div>
        <Label htmlFor="phone">Phone Number *</Label>
        <Input
          id="phone"
          type="tel"
          data-testid="application-input-phone"
          value={form.phone}
          onChange={update("phone")}
          placeholder="+91 9XXXXXXXXX"
          className="mt-2"
          required
        />
      </div>
      <div>
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          data-testid="application-input-email"
          value={form.email}
          onChange={update("email")}
          placeholder="you@example.com"
          className="mt-2"
          required
        />
      </div>
      <div>
        <Label>Experience *</Label>
        <Select
          value={form.experience}
          onValueChange={(v) => setForm((f) => ({ ...f, experience: v }))}
        >
          <SelectTrigger
            data-testid="application-select-experience"
            className="mt-2"
          >
            <SelectValue placeholder="Select experience" />
          </SelectTrigger>
          <SelectContent>
            {EXP_RANGES.map((x) => (
              <SelectItem key={x} value={x}>
                {x}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Preferred Job Type *</Label>
        <Select
          value={form.job_type}
          onValueChange={(v) => setForm((f) => ({ ...f, job_type: v }))}
        >
          <SelectTrigger
            data-testid="application-select-jobtype"
            className="mt-2"
          >
            <SelectValue placeholder="Select job type" />
          </SelectTrigger>
          <SelectContent>
            {JOB_TYPES.map((x) => (
              <SelectItem key={x} value={x}>
                {x}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label>Shift Preference *</Label>
        <Select
          value={form.shift_preference}
          onValueChange={(v) =>
            setForm((f) => ({ ...f, shift_preference: v }))
          }
        >
          <SelectTrigger
            data-testid="application-select-shift"
            className="mt-2"
          >
            <SelectValue placeholder="Select shift preference" />
          </SelectTrigger>
          <SelectContent>
            {SHIFT_PREFS.map((x) => (
              <SelectItem key={x} value={x}>
                {x}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="resume">Upload Resume (PDF / DOC, max 5MB)</Label>
        <label
          htmlFor="resume"
          className="mt-2 flex items-center justify-between gap-3 px-4 py-3 rounded-sm border-2 border-dashed border-[#E2E8F0] hover:border-[#1F6B5C] cursor-pointer transition-colors bg-white"
          data-testid="application-resume-dropzone"
        >
          <div className="flex items-center gap-3 text-sm">
            <Upload className="h-5 w-5 text-[#1F6B5C]" />
            <span className="text-[#0F172A]">
              {resume ? resume.name : "Click to upload your resume"}
            </span>
          </div>
          {resume && (
            <span className="text-xs text-[#64748B]">
              {(resume.size / 1024).toFixed(0)} KB
            </span>
          )}
        </label>
        <input
          id="resume"
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={onFileChange}
          className="hidden"
          data-testid="application-input-resume"
        />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="message">Additional Notes (optional)</Label>
        <Textarea
          id="message"
          rows={4}
          data-testid="application-input-message"
          value={form.message}
          onChange={update("message")}
          placeholder="Anything else we should know?"
          className="mt-2"
        />
      </div>
      <div className="md:col-span-2">
        <Button
          type="submit"
          disabled={submitting}
          data-testid="application-submit-button"
          className="w-full sm:w-auto bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-sm px-8 h-12"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Submitting…
            </>
          ) : (
            "Submit Application"
          )}
        </Button>
      </div>
    </form>
  );
}
